import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Download } from "lucide-react";

function exportCsv(rows) {
  const head = ["Email", "Name", "Company", "Role", "Signed up", "Subscription", "Payout connected", "Products"];
  const esc = (v) => {
    let s = String(v ?? "");
    if (/^[=+\-@]/.test(s)) s = "'" + s;
    return `"${s.replace(/"/g, '""')}"`;
  };
  const lines = [head.join(",")].concat(
    rows.map((m) =>
      [m.email, m.name, m.company, m.role, m.signed_up ? m.signed_up.slice(0, 10) : "", m.subscription_status, m.payout_connected ? "yes" : "no", m.products_count].map(esc).join(",")
    )
  );
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `vismarks-members-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const STATUS_STYLE = {
  active: "text-[#34C759] border-[#34C759]/40 bg-[#34C759]/10",
  trial: "text-[#007AFF] border-[#007AFF]/40 bg-[#007AFF]/10",
  exempt: "text-neutral-400 border-white/20 bg-white/5",
  expired: "text-[#FF9F0A] border-[#FF9F0A]/40 bg-[#FF9F0A]/10",
  none: "text-neutral-500 border-white/10 bg-white/5",
};

export default function MembersPanel() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/members");
        setRows(data);
      } catch {
        setRows([]);
      }
    })();
  }, []);

  const filtered = (rows || []).filter(
    (m) => !q || `${m.email} ${m.name || ""} ${m.company || ""} ${m.role}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="members-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-[#007AFF]" />
          <h2 className="font-black text-xl tracking-tight">Members</h2>
          {rows && <span className="text-xs text-neutral-500 font-mono">({rows.length})</span>}
        </div>
        <div className="flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, name, company…"
            data-testid="members-search"
            className="h-9 w-64 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#007AFF]"
          />
          <button
            onClick={() => exportCsv(filtered)}
            disabled={!rows || filtered.length === 0}
            data-testid="members-export-csv"
            className="h-9 inline-flex items-center gap-1.5 rounded-md border border-[#27272A] bg-[#0A0A0A] px-3 text-xs font-bold uppercase tracking-widest text-neutral-300 hover:border-[#007AFF] hover:text-white transition-colors disabled:opacity-40"
          >
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="members-table">
          <thead className="text-[10px] uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="text-left py-2 pr-3">Member</th>
              <th className="text-left py-2 pr-3">Company</th>
              <th className="text-left py-2 pr-3">Role</th>
              <th className="text-left py-2 pr-3">Subscription</th>
              <th className="text-center py-2 pr-3">Payouts</th>
              <th className="text-right py-2 pr-3">Products</th>
              <th className="text-left py-2">Signed up</th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={7} className="py-4 text-neutral-500">Loading…</td></tr>
            ) : filtered.map((m) => (
              <tr key={m.id} className="border-t border-[#27272A]">
                <td className="py-2.5 pr-3">
                  <div className="text-white font-medium">{m.name || "—"}</div>
                  <div className="text-xs text-neutral-500">{m.email}</div>
                </td>
                <td className="py-2.5 pr-3 text-neutral-400">{m.company || "—"}</td>
                <td className="py-2.5 pr-3 capitalize text-neutral-300">{m.role}</td>
                <td className="py-2.5 pr-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_STYLE[m.subscription_status] || STATUS_STYLE.none}`}>
                    {m.subscription_status}
                  </span>
                </td>
                <td className="py-2.5 pr-3 text-center">{m.payout_connected ? "✅" : "—"}</td>
                <td className="py-2.5 pr-3 text-right font-mono">{m.products_count}</td>
                <td className="py-2.5 text-neutral-500 text-xs">{m.signed_up ? new Date(m.signed_up).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
